import React, { useEffect, useState, useCallback } from "react";
import {
  CCard,
  CCardHeader,
  CCardBody,
  CRow,
  CCol,
  CSpinner,
  CButton,
  CListGroup,
  CListGroupItem,
  CBadge,
  CTooltip,
} from "@coreui/react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import CIcon from "@coreui/icons-react";
import {
  cilArrowLeft,
  cilFilter,
  cilPencil,
  cilPlus,
  cilReload,
  cilX,
} from "@coreui/icons";
import { getProductDetails } from "src/services/Products";
import StoreStockFilter from "./StoreStockFilter";
import { usePaginationState } from "src/reusables/usePaginationState";
import { Toaster } from "src/components/Toaster";
import { DataGrid } from "@mui/x-data-grid";
import { getCustomCities, getProvinces } from "src/services/Region";
import { getSalesList } from "src/services/SMD";

function ProductDetails() {
  const [productDetails, setProductDetails] = useState(null);
  const [dataRows, setDataRows] = useState([]);
  const [dataColumns, setDataColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [color, setColor] = useState("");
  const [storeFilterOpen, setStoreFilterOpen] = useState(false);
  const [storeFilters, setStoreFilters] = useState({});
  const [smdOptions, setSmdOptions] = useState([]);
  const [provOptions, setProvOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Parse URL query parameters
  const getUrlFilters = () => {
    const searchParams = new URLSearchParams(location.search);
    const urlFilters = {};

    // Get all query parameters
    for (const [key, value] of searchParams.entries()) {
      if (value) {
        urlFilters[key] = value;
      }
    }

    return urlFilters;
  };

  // Update URL with current filters
  const updateUrlWithFilters = (newFilters) => {
    const searchParams = new URLSearchParams();

    Object.entries(newFilters).forEach(([key, value]) => {
      if (value) {
        searchParams.set(key, value);
      }
    });

    const newUrl = `${location.pathname}${
      searchParams.toString() ? "?" + searchParams.toString() : ""
    }`;
    navigate(newUrl, { replace: true });
  };

  async function fetchMainData(filtersToUse = storeFilters) {
    try {
      setLoading(true);
      const res = await getProductDetails(id, filtersToUse);
      setProductDetails(res.product);
      const rows =
        res.stores &&
        res.stores.map((item, i) => {
          return {
            no: i + 1,
            id: item.id,
            name: item.name,
            provinceId: item.province_id,
            provinceName: item.provinceName,
            cityId: item.city_id,
            cityName: item.cityName,
            type: item.type,
            smdId: item.sales_id,
            smdName: item.smdName,
            stock: item.stock,
          };
        });

      setDataRows(rows);

      const columns = [
        { field: "no", headerName: "No", width: "50" },
        { field: "name", headerName: "Name", width: "200" },
        { field: "provinceName", headerName: "Provinsi", width: "150" },
        { field: "cityName", headerName: "Kota", width: "150" },
        { field: "type", headerName: "Tipe", width: "50" },
        { field: "smdName", headerName: "SMD", width: "150" },
        { field: "stock", headerName: "Stock", width: "50" },
      ];

      setDataColumns(columns);
    } catch (error) {
      setMessage("Gagal Memuat data produk");
      setColor("danger");
      console.error("Error fetching product details:", error);
    } finally {
      setLoading(false);
    }
  }

  // Fetch SMD options for filter display
  useEffect(() => {
    const fetchSMDOptions = async () => {
      try {
        const response = await getSalesList();
        const smdData = response?.data || response || [];
        const options = smdData.map((smd) => ({
          value: smd.id,
          label: `${smd.name} - ${smd.id}`,
        }));
        setSmdOptions(options);
      } catch (error) {
        console.error("Error fetching SMD options:", error);
        setSmdOptions([]);
      }
    };

    const fetchProvOptions = async () => {
      try {
        const response = await getProvinces();
        const provData = response?.data || response || [];
        const options = provData.map((prov) => ({
          value: prov.id,
          label: `${prov.nama}`,
        }));
        setProvOptions(options);
      } catch (error) {
        console.error("Error fetching Province options:", error);
        setProvOptions([]);
      }
    }

    const fetchCityOptions = async () => {
      try {
        const response = await getCustomCities();
        const cityData = response?.data || response || [];
        const options = cityData.map((city) => ({
          value: city.id,
          label: city.nama,
        }));
        setCityOptions(options);
      } catch (error) {
        console.error("Error fetching City options:", error);
        setCityOptions([]);
      }
    };

    fetchSMDOptions();
    fetchProvOptions();
    fetchCityOptions();
  }, []);

  const [paginationModel, setPaginationModel] = usePaginationState();

  // Initialize filters from URL and fetch data
  useEffect(() => {
    const urlFilters = getUrlFilters();
    setStoreFilters(urlFilters);
    fetchMainData(urlFilters);
  }, [id, location.search]); // This will run on mount AND when URL changes

  useEffect(() => {
    if (location.state?.page !== undefined) {
      setPaginationModel({
        page: location.state.page,
        pageSize: location.state.pageSize || paginationModel.pageSize,
      });
    }
  }, [location.state]);

  function handleRowClick(params) {
    navigate(`/product/details/${params.row.id}`, {
      state: {
        fromPage: paginationModel.page,
        fromPageSize: paginationModel.pageSize,
      },
    });
  }

  const handleApplyFilters = (newFilters) => {
    setStoreFilters(newFilters);
    updateUrlWithFilters(newFilters);
    setStoreFilterOpen(false);
    fetchMainData(newFilters);
  };

  // Helper functions for filter display
  const getFilterLabel = (key) => {
    const labels = {
      name: "Nama",
      smdId: "SMD",
      provinceId: "Provinsi",
      cityId: "Kota",
      type: "Tipe",
    };
    return labels[key] || key;
  };

  const getFilterValueDisplay = (key, v) => {
    if (key === "smdId") {
      const smdOption = smdOptions.find((option) => (
        option.value == v
      ))
      return smdOption ? smdOption.label : v;
    }

    if (key === "provinceId") {
      const provOpt = provOptions.find((option) => (
        option.value == v
      ))
      return provOpt ? provOpt.label : v;
    }

    if (key === "cityId") {
      const cityOpt = cityOptions.find((option) => (
        option.value == v
      ))
      return cityOpt ? cityOpt.label : v;
    }

    return v;
  };

  // Filter handlers
  const handleRemoveFilter = (key) => {
    const newFilters = { ...storeFilters, [key]: "" };
    setStoreFilters(newFilters);
    updateUrlWithFilters(newFilters);
    fetchMainData(newFilters);
  };

  const handleResetFilters = () => {
    const emptyFilters = {
      name: "",
      smdId: "",
      provinceId: "",
      cityId: "",
      type: "",
    };
    setStoreFilters(emptyFilters);
    updateUrlWithFilters(emptyFilters);
    fetchMainData(emptyFilters);
  };

  if (!productDetails) {
    return <div>Product not found</div>;
  }

  return (
    <div style={{ color: "black" }}>
      <CCard>
        <Toaster
          open={open}
          message={message}
          setOpen={setOpen}
          ahd={1500}
          color={color ?? "red"}
        />
        <CCardHeader>
          <CRow className="align-items-center">
            <CCol md={6}>
              <h4 className="mb-0">Product Details</h4>
              <small className="text-muted">ID: {productDetails.id}</small>
            </CCol>
            <CCol md={6} className="text-end">
              <CButton
                color="light"
                onClick={() =>
                  navigate(-1, {
                    state: {
                      page: location.state?.fromPage || 0,
                      pageSize: location.state?.fromPageSize || 10,
                    },
                  })
                }
                className="me-2"
              >
                <CIcon icon={cilArrowLeft} className="me-2" />
                Back
              </CButton>
              <CButton
                color="primary"
                onClick={() => navigate(`/product/actions/${id}`)}
              >
                <CIcon icon={cilPencil} className="me-2" />
                Edit Product
              </CButton>
            </CCol>
          </CRow>
        </CCardHeader>
        <CCardBody>
          <CRow className="mb-4">
            <CCol>
              <CCard className="mb-4">
                <CCardHeader>
                  <h5>Product Information</h5>
                </CCardHeader>
                <CCardBody>
                  <CListGroup>
                    <CListGroupItem>
                      <strong>Category:</strong> {productDetails.categoryName}
                    </CListGroupItem>
                    <CListGroupItem>
                      <strong>SKU:</strong> {productDetails.sku}
                    </CListGroupItem>
                    <CListGroupItem>
                      <strong>Name:</strong> {productDetails.name}
                    </CListGroupItem>
                  </CListGroup>
                </CCardBody>
              </CCard>
              <CCard>
                <CCardHeader className="p-3 p-md-4">
                  <div className="d-flex flex-column gap-3">
                    <CRow className="align-items-center g-3">
                      <CCol xs={12} md={6} className="pe-md-3">
                        <h4 className="mb-0"> Stock Availability by Store </h4>
                      </CCol>

                      <CCol xs={12} md={6} className="ps-md-3">
                        <div className="d-flex flex-wrap justify-content-md-end gap-2">
                          <CTooltip content="Refresh Data">
                            <CButton
                              color="light"
                              onClick={fetchMainData}
                              disabled={loading}
                              className="p-2"
                              shape="pill"
                            >
                              <CIcon icon={cilReload} />
                            </CButton>
                          </CTooltip>

                          <CTooltip content="Filter Data">
                            <CButton
                              color="light"
                              onClick={() => setStoreFilterOpen(true)}
                              className="p-2"
                              shape="pill"
                            >
                              <CIcon icon={cilFilter} />
                            </CButton>
                          </CTooltip>

                          <CButton
                            color="primary"
                            onClick={() => navigate("/product/actions/")}
                            className="px-3"
                          >
                            <CIcon icon={cilPlus} className="me-2" />
                            Tambah Toko
                          </CButton>
                        </div>
                      </CCol>
                    </CRow>
                    {/* Filter Chips Section */}
                    {Object.keys(storeFilters).some(
                      (key) => storeFilters[key]
                    ) && (
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        <small className="text-muted me-2">Filter Aktif:</small>
                        {Object.entries(storeFilters).map(
                          ([key, value]) =>
                            value && (
                              <CBadge
                                key={key}
                                color="primary"
                                className="d-flex align-items-center py-2 px-3"
                                shape="rounded-pill"
                              >
                                <span className="me-2">
                                  {getFilterLabel(key)}:{" "}
                                  {getFilterValueDisplay(key, value)}
                                </span>
                                <div
                                  role="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveFilter(key);
                                  }}
                                  className="d-flex align-items-center"
                                >
                                  <CIcon icon={cilX} width={14} />
                                </div>
                              </CBadge>
                            )
                        )}
                        <CButton
                          color="link"
                          size="sm"
                          className="text-decoration-none p-0 ms-2"
                          onClick={handleResetFilters}
                        >
                          Clear All
                        </CButton>
                      </div>
                    )}
                  </div>
                </CCardHeader>
                <div style={{ margin: "16px" }}>
                  <DataGrid
                    rows={dataRows}
                    columns={dataColumns}
                    onRowClick={handleRowClick}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[5, 10, 20, 25]}
                    disableRowSelectionOnClick
                    autoHeight
                    sx={{
                      "& .MuiDataGrid-cell:focus": {
                        outline: "none",
                      },
                      "& .MuiDataGrid-columnHeader:focus": {
                        outline: "none",
                      },
                    }}
                  />
                </div>
              </CCard>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>
      <StoreStockFilter
        open={storeFilterOpen}
        onClose={() => setStoreFilterOpen(false)}
        onApply={handleApplyFilters}
        initialFilters={storeFilters}
      />
    </div>
  );
}

export default ProductDetails;
